// Google Analytics 4 (gtag.js). The measurement ID comes from
// VITE_GA_MEASUREMENT_ID (.env locally, GitHub secret in CI); without it,
// nothing is loaded. Route changes are tracked by GA4's enhanced
// measurement ("page changes based on browser history events"), so no
// manual page_view calls are needed.
//
// Consent: gtag.js is only loaded after the visitor accepts analytics in
// the cookie banner (CookieConsent.tsx). Until then no request goes to
// Google and no _ga cookies are set. Consent Mode signals are sent too, so
// a later "Decline" from the footer's Cookie settings stops collection on
// a page where gtag.js is already loaded.

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as
  | string
  | undefined;

export type ConsentChoice = "granted" | "denied";

const STORAGE_KEY = "hbt-cookie-consent";
/** Fired to reopen the cookie banner (footer "Cookie settings" link). */
export const OPEN_COOKIE_SETTINGS = "hbt:open-cookie-settings";

export const analyticsEnabled = Boolean(GA_MEASUREMENT_ID) && import.meta.env.PROD;

let loaded = false;

export function getConsent(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

function ensureGtag() {
  if (typeof window.gtag === "function") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the arguments object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  // Everything starts denied; ads storage is never granted because the
  // site doesn't use Google Ads.
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

function loadAnalytics() {
  if (loaded || !analyticsEnabled) return;
  loaded = true;
  ensureGtag();
  window.gtag("consent", "update", { analytics_storage: "granted" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID!)}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);
}

/** Removes the _ga / _ga_<id> cookies GA set on this domain. */
function deleteAnalyticsCookies() {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.replace(/^www\./, "")}`];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (!name.startsWith("_ga")) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
}

export function setConsent(choice: ConsentChoice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Storage blocked: the choice applies to this page view only.
  }
  if (choice === "granted") {
    loadAnalytics();
  } else {
    if (loaded) window.gtag("consent", "update", { analytics_storage: "denied" });
    deleteAnalyticsCookies();
  }
}

/** Called once at startup: loads GA only if the visitor accepted earlier. */
export function initAnalytics() {
  if (getConsent() === "granted") loadAnalytics();
}
